"use client"

import React, { useEffect, useState } from "react"
import styles from "./styles.module.scss"
import CategorySectionHeader from "../category-section-header/category-section-header"
import { useCategory } from "@/src/context/category/category-context"
import SvgArrow from "@/src/assets/svgs/collection/arrow-right.svg"
import { CategoriesFunctions } from "../../functions"
import LoadingCategories from "./loading-categories/loading-categories"
import ProductCard from "../product-card/product-card"
import Category from "@/src/interfaces/catalog/category"
import Items from "@/src/interfaces/catalog/items"
import { useMerchant } from "@/src/context/merchant/merchant-context"
import { HighlightText } from "@/src/helpers/highlight-text"
import { useVisibleCategories } from "@/src/context/product/category-product-context"
import FullProduct from "@/src/components/product/full-product/full-product"
import NoProductsFound from "./no-products-found/no-products-found"
import { useAppAccessToken } from "@/src/context/app-access-token/app-access-token-context"
import CacheHandler from "@/src/core/cache/cache-handler"
import { useGoogleAnalytics } from "@/src/context/google-analytics/google-analytics-context"

type CategoryProducts = {
    [categoryId: string]: Items.ItemResponse[] // GUID como chave.
}

type LoadingCategoriesState = {
    [categoryId: string]: boolean // Estado de carregamento por categoria.
}

export default function Categories() {
    const {
        visibleCategories,
        toggleCategoryVisibility,
        openCategory,
        collapseAllCategories,
        activeCategoryIndex,
        firstCategoryLoaded,
        setFirstCategoryLoaded,
        isScrollingToSection
    } = useVisibleCategories()

    const { categories, categoriesBySearch, loading, errorMessage, searchText } = useCategory()
    const { merchantSummary, merchantDetailed, localMode } = useMerchant()
    const { appAccessToken } = useAppAccessToken()
    const { viewItemGA } = useGoogleAnalytics()

    const [loadingCategories, setLoadingCategories] = useState<LoadingCategoriesState>({})
    const [alreadySearchedCategories, setAlreadySearchedCategories] = useState<string[]>([])
    const [currentCategories, setCurrentCategories] = useState<Category.Category[] | undefined>(categories)

    const [categoryProducts, setCategoryProducts] = useState<CategoryProducts>({})
    const [fetchedItems, setFetchedItems] = useState<CategoryProducts>()
    const [searchedItems, setSearchedItems] = useState<{ items: Items.ItemResponse[], categoryId: string }[]>([])

    const [product, setProduct] = useState<Items.ItemResponse>()
    const [modalProduct, setModalProduct] = useState(false)

    const isSearch = categoriesBySearch?.categories && categoriesBySearch.categories.length > 0

    const fetchProducts = async (categoryId: string, force: boolean = false, isFirstCategory = false) => {
        if (!force && (categoryProducts[categoryId] || alreadySearchedCategories.some(e => e === categoryId))) return

        setLoadingCategories((prevState) => ({
            ...prevState,
            [categoryId]: true,
        }))

        try {
            const params: Items.ItemsSearchParams = {
                merchantId: merchantSummary?.id!,
                categoryId: categoryId,
                sellLocal: !!localMode
            }

            const items = await CacheHandler.getItems(params, appAccessToken)

            if ((items && items.length < 1) || !items)
                return

            setOrderedItems(items, categoryId)

            if (isSearch) {
                setFetchedItems((prevState) => ({
                    ...prevState,
                    [categoryId]: items,
                }))
            }

            if (isFirstCategory)
                setFirstCategoryLoaded(true)
        }
        catch (error) {
            console.error(`Failed to fetch products for category ${categoryId}:`, error)
        }
        finally {
            setLoadingCategories((prevState) => ({
                ...prevState,
                [categoryId]: false,
            }))
        }
    }

    const setOrderedItems = (items: Items.ItemResponse[], categoryId: string) => {
        if (merchantSummary?.itemsSortType === 'Alphabetical') {
            setCategoryProducts((prevState) => ({
                ...prevState,
                [categoryId]: items.sort((a, b) => a.name.localeCompare(b.name)),
            }))
        }
        else {
            setCategoryProducts((prevState) => ({
                ...prevState,
                [categoryId]: items,
            }))
        }
    }

    const handleCardClick = (p: Items.ItemResponse) => {
        setProduct(p)
        setModalProduct(true)

        viewItemGA({
            currency: "BRL",
            value: p.salePrice || 0,
            items: [
                {
                    item_id: p.id,
                    item_name: p.name
                }
            ]
        })
    }

    const handleModalClose = () => {
        setModalProduct(false)
        setProduct(undefined)
    }

    const renderCardList = (categoryId: string, index: number) => {
        // Joga o título das categorias mais para baixo não deixando visíveis na tela, evitando múltiplos carregamentos inciais.
        const heightToVisibleCategory = '20vh'

        if (loadingCategories[categoryId])
            return <LoadingCategories loadingOnProducts />

        if (!loadingCategories[categoryId] && !categoryProducts[categoryId])
            return <div style={{ height: merchantSummary?.categoriesViewMode === 'Compact' ? '0' : heightToVisibleCategory }} />

        return (
            <div className={`${styles.cardsList} cards-list`}>
                {
                    visibleCategories[index] &&
                    categoryProducts[categoryId]?.map((product: Items.ItemResponse) => (
                        <ProductCard setProduct={p => handleCardClick(p)} key={product.id} product={product} />
                    ))
                }
            </div>
        )
    }

    const handleCategoryClick = async (index: number, id: string) => {
        toggleCategoryVisibility(index)

        if (merchantSummary?.categoriesViewMode === 'Compact')
            await fetchProducts(id)
    }

    useEffect(() => {
        setTimeout(() => {
            if ((isSearch) && searchText)
                HighlightText.highlightSearchText(searchText, 'cards-list')
        }, 10)
    }, [currentCategories, categoryProducts])

    useEffect(() => {
        const categoriesList = document.querySelectorAll(".content-categories")

        if (!categoriesList.length || typeof document === "undefined") return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const element = entry.target as HTMLElement
                    const categoryId = element.getAttribute("id")?.replace("section-", "")
                    const isFirstCategory = element === categoriesList[0]

                    if (element.getAttribute('data-id') === 'promo') return

                    if (entry.isIntersecting && categoryId && !isScrollingToSection) {
                        setAlreadySearchedCategories((prevState) => {
                            if (prevState.includes(categoryId))
                                return prevState

                            if (localMode !== undefined)
                                fetchProducts(categoryId, false, isFirstCategory)

                            return [...prevState, categoryId]
                        })

                        observer.unobserve(element)
                    }
                })
            },
            {
                rootMargin: "0px 0px 50px 0px",
                threshold: 0.5,
            }
        )

        if (merchantSummary?.categoriesViewMode !== 'Compact')
            categoriesList.forEach((category) => observer.observe(category))

        return () => observer.disconnect()
    }, [currentCategories, categoriesBySearch, localMode, firstCategoryLoaded, isScrollingToSection, appAccessToken])

    useEffect(() => {
        if (activeCategoryIndex !== null && visibleCategories.hasOwnProperty(activeCategoryIndex) && !visibleCategories) {
            toggleCategoryVisibility(activeCategoryIndex)
        }
    }, [activeCategoryIndex])

    useEffect(() => {
        if (isSearch) {
            const _currentCategories = categoriesBySearch.categories.map(category => ({
                id: category.id,
                name: category.name,
                sortIndex: category.sortIndex
            }))

            setCurrentCategories(_currentCategories as Category.Category[])

            const newSearchedItems = categoriesBySearch.categories.map(i => ({
                items: i.items,
                categoryId: i.id
            }))

            setSearchedItems(prev => {
                const uniqueItems = newSearchedItems.filter(newItem =>
                    !prev.some(prevItem => prevItem.categoryId === newItem.categoryId)
                )
                return [...prev, ...uniqueItems]
            })

            categoriesBySearch.categories.forEach((i) => {
                setOrderedItems(i.items, i.id)

                if (merchantDetailed?.model?.categoriesViewMode === 'Compact')
                    openCategory(i.id)
            })
        }
        else {
            setCurrentCategories(categories)

            if (fetchedItems)
                setSearchedItems([])

            if (merchantDetailed?.model?.categoriesViewMode === 'Compact' && !categoriesBySearch?.categories)
                collapseAllCategories()
        }
    }, [categoriesBySearch, categories])

    useEffect(() => {
        if (categoriesBySearch?.categories === undefined) {
            const categoriesToRefetch = [...searchedItems]

            categoriesToRefetch.forEach(searched => {
                setCategoryProducts(prev => ({
                    ...prev,
                    [searched.categoryId]: []
                }))
                setAlreadySearchedCategories(prev =>
                    prev.filter(id => id !== searched.categoryId)
                )
            })

            categoriesToRefetch.forEach(searched => {
                fetchProducts(searched.categoryId, true)
            })
        }
    }, [categoriesBySearch])

    if (loading) return <LoadingCategories />

    if (errorMessage) return <></>

    return (
        <>
            {
                (modalProduct && product) &&
                <FullProduct
                    product={product}
                    isOpen={modalProduct}
                    onClose={handleModalClose}
                />
            }
            <section className={styles.container}>
                {
                    categoriesBySearch?.categories && categoriesBySearch.categories.length < 1 ?
                        <NoProductsFound />
                        :
                        currentCategories && currentCategories.map((category, index) => {
                            const shouldRender = index === 0 || firstCategoryLoaded || merchantSummary?.categoriesViewMode === 'Compact'

                            if (!shouldRender) return null

                            return (
                                <div
                                    key={category.id}
                                    id={CategoriesFunctions.formatStrToNavigation("section", category.id)}
                                    className="content-categories"
                                    data-toggle={visibleCategories[index] ? "on" : "off"}
                                >
                                    <div
                                        className={`head-title ${styles.headTitleArea} ${!visibleCategories[index] ? styles.headTitleAreaClosed : ''}`}
                                        onClick={() => handleCategoryClick(index, category.id)}
                                    >
                                        <CategorySectionHeader
                                            id={category.id}
                                            className={styles.headTitle}
                                            showSeeAll={false}
                                            title={category.name}
                                            type={"categories"}
                                        />
                                        <SvgArrow className={`${styles.arrowShowCategory} ${visibleCategories[index] ? styles.showRotateArrow : styles.hideRotateArrow}`} />
                                    </div>
                                    {renderCardList(category.id, index)}
                                </div>
                            )
                        })
                }
            </section>
        </>
    )
}